import { useMemo, useCallback, useState } from 'react';
import { clsx } from 'clsx';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SparklesIcon, WifiIcon, ArrowPathIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import {
  completeInspection,
  fetchInspection,
  fetchReport,
  fetchReportPresets,
  generateReport,
  fetchPhotoUrls,
  type PhotoUrl,
} from '../lib/apiClient';
import { ReportSummary } from '../components/reports/ReportSummary';
import { formatDistanceToNow } from '../lib/time';
import { useInspectionJobs } from '../hooks/useInspectionJobs';
import { BulkPhotoUploader } from '../components/inspections/BulkPhotoUploader';
import { PaywallModal } from '../components/billing/PaywallModal';

export function ItemDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState<string | undefined>();

  const {
    data: inspection,
    isLoading,
    refetch: refetchInspection
  } = useQuery({
    queryKey: ['inspection', id],
    queryFn: () => fetchInspection(id ?? ''),
    enabled: Boolean(id)
  });

  // Callback when inspection is updated via socket
  const handleInspectionUpdated = useCallback(() => {
    refetchInspection();
  }, [refetchInspection]);

  const { jobs, activeJob, currentProgress, isLoading: jobsLoading, isConnected } = useInspectionJobs(id, handleInspectionUpdated);

  const { data: presets = [] } = useQuery({
    queryKey: ['report-presets'],
    queryFn: fetchReportPresets,
  });

  // Fetch photo URLs for displaying images
  const { data: photoUrls = [] } = useQuery({
    queryKey: ['photo-urls', id],
    queryFn: () => fetchPhotoUrls(id ?? ''),
    enabled: Boolean(id) && Boolean(inspection?.rooms?.length),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create a map of photoId -> url for quick lookup
  const photoUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    photoUrls.forEach((p: PhotoUrl) => map.set(p.photoId, p.url));
    return map;
  }, [photoUrls]);

  const { data: report, refetch: refetchReport } = useQuery({
    queryKey: ['report', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        return await fetchReport(id);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(id)
  });

  const completeMutation = useMutation({
    mutationFn: () => completeInspection(id ?? ''),
    onSuccess: () => refetchInspection()
  });

  const generateReportMutation = useMutation({
    mutationFn: () =>
      generateReport({
        inspectionId: id ?? '',
        introduction: 'Automated inspection overview generated via InspectAI.',
        conclusion: 'Resolve outstanding issues within 7 days to keep the property tenant-ready.'
      }),
    onSuccess: async () => {
      await refetchReport();
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 402) {
        setPaywallMessage(error.response?.data?.message);
        setPaywallOpen(true);
      }
    }
  });

  const conditionDistribution = useMemo(() => {
    if (!inspection) return [];
    return inspection.rooms.map((room) => ({
      label: room.name,
      condition: room.conditionRating ?? 'unrated',
      issues: room.photos.flatMap((photo) => photo.issues ?? [])
    }));
  }, [inspection]);

  const reportPresetLabel = useMemo(() => {
    if (!inspection) return 'Not configured';
    if (!inspection.reportPresetId) {
      return 'Default preset';
    }
    const preset = presets.find((item) => item._id === inspection.reportPresetId);
    return preset ? preset.name : `Preset ${inspection.reportPresetId}`;
  }, [inspection, presets]);

  if (isLoading || !inspection) {
    return <div className="glass-card h-64 animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <div className="glass-card flex flex-wrap items-center justify-between gap-4 p-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Inspection detail</p>
          <h2 className="text-2xl font-semibold text-foreground">
            {typeof inspection.propertyId === 'string' ? inspection.propertyId : inspection.propertyId.name}
          </h2>
          <p className="text-sm text-muted-foreground">Last updated {formatDistanceToNow(inspection.updatedAt)}</p>
          <p className="text-xs text-muted-foreground">Report preset: {reportPresetLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={completeMutation.isPending}
            onClick={() => completeMutation.mutate()}
            className="btn-glass btn-secondary focus-ring text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
          >
            Mark completed
          </button>
          <button
            type="button"
            disabled={generateReportMutation.isPending}
            onClick={() => generateReportMutation.mutate()}
            className="btn-glass btn-primary focus-ring inline-flex items-center gap-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            data-onboarding="report-generate"
          >
            <SparklesIcon className="h-4 w-4" />
            Generate report
          </button>
        </div>
      </div>

      {/* Active Processing Banner */}
      {activeJob && currentProgress && (
        <div className="glass-card overflow-hidden p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowPathIcon className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="font-semibold text-foreground">
                  {currentProgress.message || 'Processing photos...'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {currentProgress.processedUnits}/{currentProgress.totalUnits} photos processed
                </p>
              </div>
            </div>
            <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              {currentProgress.progress}%
            </span>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/40 dark:bg-white/5">
            <div
              className="h-2 rounded-full bg-primary transition-all duration-300"
              style={{ width: `${currentProgress.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Photo Upload Section */}
      <BulkPhotoUploader
        inspectionId={id ?? ''}
        onUploadComplete={() => {
          refetchInspection();
          queryClient.invalidateQueries({ queryKey: ['jobs', id] });
        }}
        onUpgradeRequired={(message) => {
          setPaywallMessage(message);
          setPaywallOpen(true);
        }}
      />

      <section className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {/* Show rooms only if there are any */}
          {inspection.rooms.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <p className="text-lg font-medium text-foreground">No rooms detected yet</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Upload photos above and our AI will automatically classify them into rooms and detect issues.
              </p>
            </div>
          ) : null}
          {inspection.rooms.map((room) => (
            <article key={room._id} className="glass-card space-y-4 p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">{room.name}</h3>
                  <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{room.photos.length} photos</p>
                </div>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {room.conditionRating ?? 'unrated'}
                </span>
              </div>
              {room.aiSummary ? (
                <p className="text-sm leading-relaxed text-foreground">{room.aiSummary}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  AI summary will appear here after running photo analysis for this room.
                </p>
              )}

              {room.actions && room.actions.length > 0 && (
                <div className="rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
                  <p className="font-semibold">Suggested actions</p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    {room.actions.map((action) => (
                      <li key={action}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}

              {room.photos.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-3">
                  {room.photos.map((photo) => {
                    const imageUrl = photoUrlMap.get(photo._id);
                    return (
                      <figure
                        key={photo._id}
                        className="rounded-xl border border-white/10 bg-white/20 p-3 text-xs text-muted-foreground dark:bg-white/10"
                      >
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={photo.originalFilename}
                            className="aspect-video w-full rounded-lg object-cover bg-muted"
                            loading="lazy"
                          />
                        ) : (
                          <div className="aspect-video w-full rounded-lg bg-gradient-to-br from-muted to-muted/40 flex items-center justify-center">
                            <PhotoIcon className="h-8 w-8 text-muted-foreground/50" />
                          </div>
                        )}
                        <figcaption className="mt-2">
                          <p className="font-medium text-foreground">{photo.originalFilename}</p>
                          {photo.aiSummary && (
                            <p className="mt-1 text-muted-foreground">{photo.aiSummary}</p>
                          )}
                          {photo.issues && photo.issues.length > 0 ? (
                            <ul className="mt-2 space-y-1">
                              {photo.issues.map((issue, index) => (
                                <li key={`${issue.label}-${index}`} className={clsx(
                                  'flex items-center gap-1',
                                  issue.severity === 'high' && 'text-red-500',
                                  issue.severity === 'medium' && 'text-yellow-500',
                                  issue.severity === 'low' && 'text-green-500'
                                )}>
                                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
                                  {issue.label}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="mt-1 text-green-600">✓ No issues detected</p>
                          )}
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="space-y-4">
          <div className="glass-card space-y-4 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Processing jobs</h3>
                <p className="text-xs text-muted-foreground">Photo analysis and report generation progress.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{jobs.length} jobs</span>
                <span 
                  className={clsx(
                    'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                    isConnected ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  )}
                  title={isConnected ? 'Real-time updates connected' : 'Disconnected - updates may be delayed'}
                >
                  <WifiIcon className="h-3 w-3" />
                  {isConnected ? 'Live' : 'Offline'}
                </span>
              </div>
            </div>
            {jobsLoading && <div className="h-2 w-full animate-pulse rounded-full bg-muted" />}
            {!jobsLoading && jobs.length === 0 && (
              <p className="text-sm text-muted-foreground">No jobs yet. Upload photos to kick off analysis.</p>
            )}
            {!jobsLoading && jobs.length > 0 && (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <article key={job._id} className="rounded-2xl border border-white/15 bg-white/40 p-4 text-sm dark:bg-white/10">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-foreground">{job.type}</p>
                        <p className="text-xs text-muted-foreground">Updated {formatDistanceToNow(job.updatedAt)}</p>
                      </div>
                      <span
                        className={clsx(
                          'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
                          job.status === 'completed' && 'bg-primary/10 text-primary',
                          job.status === 'failed' && 'bg-[hsl(var(--destructive)/0.1)] text-[hsl(var(--destructive))]',
                          job.status === 'processing' && 'bg-warning/10 text-warning',
                          job.status === 'queued' && 'bg-muted text-muted-foreground',
                          job.status === 'pending' && 'bg-muted text-muted-foreground'
                        )}
                      >
                        {job.status}
                      </span>
                    </div>
                    <div className="mt-3">
                      <div className="h-2 w-full rounded-full bg-white/40 dark:bg-white/5">
                        <div
                          className={clsx(
                            'h-2 rounded-full transition-all',
                            job.status === 'failed'
                              ? 'bg-[hsl(var(--destructive))]'
                              : job.status === 'completed'
                              ? 'bg-primary'
                              : 'bg-[hsl(var(--primary))]'
                          )}
                          style={{ width: `${Math.min(job.progress ?? 0, 100)}%` }}
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {job.processedUnits}/{job.totalUnits || '?'} units
                        </span>
                        {job.lastError && job.status === 'failed' && <span className="text-[hsl(var(--destructive))]">{job.lastError}</span>}
                      </div>
                    </div>
                    {job.events.length > 0 && (
                      <p className="mt-3 text-xs text-muted-foreground">
                        {job.events[job.events.length - 1].message ?? 'Progress updated'}
                      </p>
                    )}
                    {job.result && typeof job.result.summary === 'string' && (
                      <div className="mt-3 rounded-xl border border-white/15 bg-white/60 p-3 text-xs text-foreground dark:bg-white/10">
                        <p className="font-semibold text-muted-foreground">Result</p>
                        <p className="mt-1 whitespace-pre-line">{job.result.summary}</p>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-foreground">Condition distribution</h3>
            <ul className="mt-4 space-y-3 text-sm">
              {conditionDistribution.map((entry) => (
                <li key={entry.label} className="flex items-center justify-between">
                  <span>{entry.label}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {entry.condition}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <ReportSummary
            report={report ?? null}
            onUpgradeRequired={(message) => {
              setPaywallMessage(message);
              setPaywallOpen(true);
            }}
          />
        </div>
      </section>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        message={paywallMessage}
      />
    </div>
  );
}
